import React from "react";
import { useNavigate } from "react-router-dom";
import { Compass, Map, Flag, AlertTriangle, ChevronRight } from "lucide-react";

function PillarCard({ pillar }) {
  return (
    <div className="rounded-2xl border border-ink-200 bg-white p-5 shadow-card" data-testid={`pillar-${pillar.title}`}>
      <h4 className="text-[15px] font-bold text-blue-800">{pillar.title}</h4>
      {pillar.objective && <p className="mt-1 text-[13px] text-ink-600 leading-relaxed">{pillar.objective}</p>}
      {pillar.owner && (
        <span className="mt-2 inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full bg-ink-100 text-ink-600">
          Owner: {pillar.owner}
        </span>
      )}
      {pillar.actions?.length > 0 && (
        <ul className="mt-3 space-y-1 text-[12px] text-ink-700 list-disc list-inside">
          {pillar.actions.map((a, i) => <li key={i}>{a}</li>)}
        </ul>
      )}
      {pillar.kpis?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {pillar.kpis.map((k, i) => (
            <span key={i} className="text-[11px] px-2 py-0.5 rounded-md bg-ink-50 text-ink-500 border border-ink-200">{k}</span>
          ))}
        </div>
      )}
      {pillar.linked_priorities?.length > 0 && (
        <div className="mt-3 pt-3 border-t border-ink-100 space-y-1">
          <span className="text-[11px] uppercase tracking-wider text-ink-400">Live priorities</span>
          {pillar.linked_priorities.map((lp, i) => (
            <div key={i} className="flex items-center gap-2 text-[12px]" data-testid={`pillar-linked-${pillar.title}-${i}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${lp.in_progress ? "bg-blue-600" : "bg-status-drifting"}`} />
              <span className="text-ink-700">{lp.title}</span>
              <span className="text-ink-400">· {lp.owner_title}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RoadmapPhase({ phase, isLast }) {
  return (
    <div className="flex gap-3" data-testid={`roadmap-phase-${phase.phase}`}>
      <div className="flex flex-col items-center">
        <div className="w-2.5 h-2.5 rounded-full bg-blue-800 mt-1.5" />
        {!isLast && <div className="w-px flex-1 bg-ink-200 mt-1" />}
      </div>
      <div className="pb-5 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-[13px] font-bold text-blue-800">{phase.phase}</span>
          <span className="text-[11px] text-ink-400">{phase.when}</span>
        </div>
        {phase.actions?.length > 0 && (
          <ul className="mt-1 space-y-0.5 text-[12px] text-ink-600 list-disc list-inside">
            {phase.actions.map((a, i) => <li key={i}>{a}</li>)}
          </ul>
        )}
      </div>
    </div>
  );
}

export default function StrategicRoadmapPanel({ vision }) {
  const navigate = useNavigate();
  if (!vision) return null;

  if (!vision.has_strategy) {
    return (
      <div className="rounded-xl border border-ink-200 bg-white p-8 text-center" data-testid="strategic-roadmap-empty">
        <Compass className="w-8 h-8 text-ink-300 mx-auto" />
        <p className="mt-3 text-[15px] font-semibold text-blue-800">No strategy deployed yet</p>
        <p className="mt-1 text-[13px] text-ink-500 max-w-md mx-auto">{vision.message}</p>
        <button onClick={() => navigate("/projectile")}
                className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-semibold px-4 py-2 rounded-lg bg-blue-800 text-white hover:bg-blue-900"
                data-testid="roadmap-goto-trajectory">
          Go to Company Trajectory <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="strategic-roadmap-panel">
      <div className="rounded-2xl border border-ink-200 bg-gradient-to-br from-ink-950 to-blue-900 p-5 text-white">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-ink-300">
          <Compass className="w-3.5 h-3.5" /> Strategic Roadmap · v{vision.version}
        </div>
        <p className="mt-1.5 text-[17px] font-bold" data-testid="roadmap-target-headline">
          {vision.target_revenue_cr ? `₹${vision.target_revenue_cr}Cr in ${vision.horizon_years} years` : "Strategic Target not set"}
        </p>
        {vision.focus && <p className="text-[13px] text-ink-300 mt-0.5">Focus: {vision.focus}</p>}
      </div>

      {vision.dependency_note && (
        <div className="flex items-start gap-2.5 rounded-lg border border-status-drifting-border bg-status-drifting-bg p-3" data-testid="roadmap-dependency-note">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-status-drifting-text" />
          <p className="text-[12px] text-status-drifting-text">{vision.dependency_note}</p>
        </div>
      )}

      {vision.pillars?.length > 0 && (
        <div>
          <h3 className="text-[13px] font-semibold uppercase tracking-wider text-ink-500 mb-3 flex items-center gap-1.5">
            <Flag className="w-3.5 h-3.5" /> Strategic Pillars
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {vision.pillars.map((p, i) => <PillarCard key={i} pillar={p} />)}
          </div>
        </div>
      )}

      {vision.roadmap_90d?.length > 0 && (
        <div className="rounded-2xl border border-ink-200 bg-white p-5 shadow-card" data-testid="roadmap-90d">
          <h3 className="text-[13px] font-semibold uppercase tracking-wider text-ink-500 mb-3 flex items-center gap-1.5">
            <Map className="w-3.5 h-3.5" /> 90-Day Roadmap
          </h3>
          {vision.roadmap_90d.map((r, i) => (
            <RoadmapPhase key={i} phase={r} isLast={i === vision.roadmap_90d.length - 1} />
          ))}
        </div>
      )}

      {vision.milestones?.length > 0 && (
        <div className="rounded-2xl border border-ink-200 bg-white p-5 shadow-card" data-testid="roadmap-milestones">
          <h3 className="text-[13px] font-semibold uppercase tracking-wider text-ink-500 mb-3">Milestones</h3>
          <ul className="space-y-1.5 text-[13px] text-ink-700 list-disc list-inside">
            {vision.milestones.map((m, i) => <li key={i}>{typeof m === "string" ? m : (m.title || JSON.stringify(m))}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}
